/**
 * barrels.ts — Breakable Barrel System
 * TheBurntPeanut Simulator 9000
 *
 * Barrels are scattered around the map. Press E to smash them open.
 * Contents (gold/XP/both) are credited instantly — no need to walk over drops.
 *
 * Small barrel:  15–30 gold  |  8–18 XP
 * Large barrel:  35–65 gold  |  20–45 XP
 * "Both" barrels split between gold and XP.
 */

import {
  BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH,
  BORDER_COLLISION_WEST,  BORDER_COLLISION_EAST,
} from './borderWalls';

export type BarrelType  = 'gold' | 'xp' | 'both';
export type BarrelSize  = 'small' | 'large';
export type BarrelState = 'intact' | 'broken';

export interface Barrel {
  id: string;
  x: number;
  y: number;
  type: BarrelType;
  size: BarrelSize;
  state: BarrelState;
  goldValue: number;
  xpValue: number;
}

// ─── Constants ────────────────────────────────────────────────

export const BARREL_INTERACT_RADIUS = 70;   // world units
export const BARREL_MIN_SPACING     = 280;  // min distance between barrels
export const BARREL_SPRITE_SMALL    = 60;   // rendered height (world units)
export const BARREL_SPRITE_LARGE    = 80;

// Sprite image paths (place these PNGs in client/public/)
export const BARREL_SPRITE_URL_XP   = '/props/barrel_diamond_v2.png';
export const BARREL_SPRITE_URL_GOLD = '/props/barrel_gold.png';
export const BARREL_SPRITE_URL_BOTH = '/props/barrel_gold_diamond_v2.png';

// ─── Seeded RNG ───────────────────────────────────────────────

function makeRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

// ─── Generation ───────────────────────────────────────────────

/**
 * Place barrels in open ground only — never inside rooms.
 */
export function generateBarrels(
  seed: number,
  worldSize: number,
  rooms: Array<{ x: number; y: number; size: number }>,
  spawnPoint: { x: number; y: number },
): Barrel[] {
  const rng = makeRng(seed);
  const barrels: Barrel[] = [];
  let idCounter = 0;

  function overlaps(x: number, y: number): boolean {
    if (barrels.some(b => Math.hypot(b.x - x, b.y - y) < BARREL_MIN_SPACING)) return true;
    if (Math.hypot(x - spawnPoint.x, y - spawnPoint.y) < 400) return true;
    return false;
  }

  function makeBarrel(x: number, y: number): Barrel {
    const r1 = rng(), r2 = rng(), r3 = rng();
    const size: BarrelSize = r1 < 0.45 ? 'large' : 'small';
    const type: BarrelType = r2 < 0.38 ? 'gold' : r2 < 0.70 ? 'xp' : 'both';

    let goldValue = 0, xpValue = 0;
    if (size === 'small') {
      if (type === 'gold' || type === 'both') goldValue = 15 + Math.floor(r3 * 16);
      if (type === 'xp'   || type === 'both') xpValue  =  8 + Math.floor(rng() * 11);
    } else {
      if (type === 'gold' || type === 'both') goldValue = 35 + Math.floor(r3 * 31);
      if (type === 'xp'   || type === 'both') xpValue  = 20 + Math.floor(rng() * 26);
    }

    return {
      id: `barrel_${idCounter++}`,
      x, y, type, size, state: 'intact', goldValue, xpValue,
    };
  }

  // ── Open-world barrels only — never inside rooms ──
  // Spawn bounds = inside the actual border walls (not a % guess).
  // 80wu inner padding so barrels don't spawn right at the collision line.
  const ROOM_BUFFER = 80;
  const PAD = 80;
  const xMin = BORDER_COLLISION_WEST + PAD;
  const xMax = worldSize - BORDER_COLLISION_EAST - PAD;
  const yMin = BORDER_COLLISION_NORTH + PAD;
  const yMax = worldSize - BORDER_COLLISION_SOUTH - PAD;

  const worldCount = Math.floor(rooms.length * 0.91) + 16; // +30% more barrels

  let attempts = 0;
  while (barrels.length < worldCount && attempts < 8000) {
    attempts++;
    const x = xMin + rng() * (xMax - xMin);
    const y = yMin + rng() * (yMax - yMin);

    // Reject if inside any room footprint (using room center + buffer)
    const inRoom = rooms.some(r => {
      const rcx = r.x + r.size / 2;
      const rcy = r.y + r.size / 2;
      const half = r.size / 2 + ROOM_BUFFER;
      return Math.abs(x - rcx) < half && Math.abs(y - rcy) < half;
    });

    if (!inRoom && !overlaps(x, y)) { barrels.push(makeBarrel(x, y)); }
  }

  return barrels;
}

// ─── Helpers ──────────────────────────────────────────────────

// BETA 14.53 — Signature changed from (barrel, {x, y}) to (barrel, px, py) to
// eliminate the object literal allocation at every call site. Squared distance
// avoids sqrt. Same pattern as isPlayerNearChest fix.
export function isPlayerNearBarrel(
  barrel: Barrel,
  px: number,
  py: number,
): boolean {
  if (barrel.state !== 'intact') return false;
  const dx = px - barrel.x;
  const dy = py - barrel.y;
  return dx * dx + dy * dy <= BARREL_INTERACT_RADIUS * BARREL_INTERACT_RADIUS;
}

